import AsyncStorage from '@react-native-async-storage/async-storage';
import { getLocales } from 'expo-localization';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import de from '@/locales/de.json';

export const supportedLanguages = ['de', 'en'] as const;
export type Locale = (typeof supportedLanguages)[number];

const STORAGE_KEY = '@zoukessence/locale';

// Synchronous init with German only, so static rendering emits real copy and
// the first client render matches the exported HTML. English is code-split and
// loaded on demand; the visitor's preference is applied post-mount via
// applyPreferredLocale().
void i18n.use(initReactI18next).init({
  resources: {
    de: { translation: de },
  },
  lng: 'de',
  fallbackLng: 'de',
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  initAsync: false,
});

function detectLocale(): Locale {
  try {
    const code = getLocales()[0]?.languageCode ?? 'de';
    if (code?.toLowerCase().startsWith('de')) return 'de';
    if (code?.toLowerCase().startsWith('en')) return 'en';
  } catch {
    /* noop */
  }
  return 'de';
}

async function ensureResources(lng: Locale) {
  if (lng === 'en' && !i18n.hasResourceBundle('en', 'translation')) {
    const en = await import('@/locales/en.json');
    i18n.addResourceBundle('en', 'translation', (en as { default?: object }).default ?? en);
  }
}

/** Client-only: switch to the stored or browser-detected locale after mount. */
export async function applyPreferredLocale() {
  let stored: string | null = null;
  try {
    stored = await AsyncStorage.getItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  const lng = stored === 'de' || stored === 'en' ? stored : detectLocale();
  if (lng !== i18n.language) {
    await ensureResources(lng);
    await i18n.changeLanguage(lng);
  }
}

export async function changeLocale(next: Locale) {
  await ensureResources(next);
  await i18n.changeLanguage(next);
  try {
    await AsyncStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* noop */
  }
}

export { i18n };
